import { FC, useMemo, useRef, useEffect } from "react";
import Highcharts from "highcharts";
import { Box } from "@mui/material";
import moment from "moment";
import Stack from "@mui/material/Stack";
import { OffHoursRecommendationsChartColors } from "../../../../utils/constants/constants";
import { OffHoursRecommendationsChartData } from "../../../../utils/types/types";
import { ChartErrorBoundary } from "@/storybook/c8r-highcharts/ChartErrorBoundary";
import {
  formatDate,
  dateFromUtcByTimeZone,
} from "@/utils/helpers/date-time/datetime-format";
import { LegendContent } from "@/components/pages/recommendations/components/off-hours-recommendations/components/table/subrow/charts/LegendContent";
import { generateOffHoursHighChartData } from "@/components/pages/recommendations/components/off-hours-recommendations/components/table/subrow/charts/helpers/generateOffHoursHighChartData";
import { generateYAxisCpuAndDbTicks } from "@/components/pages/recommendations/components/off-hours-recommendations/components/table/subrow/charts/helpers/generateYAxisCpuAndDbTicks";
import { offHoursChartTooltipOptions } from "@/components/pages/recommendations/components/off-hours-recommendations/components/table/subrow/charts/helpers/offHoursChartTooltipOptions";
import { getYAxises } from "@/components/pages/recommendations/components/off-hours-recommendations/components/table/subrow/charts/helpers/offHoursChartHelpers";

interface OffHoursRecommendationsChartProps {
  data: OffHoursRecommendationsChartData[];
  lineInCenter?: boolean;
  withDbConnections: boolean;
  chartHeight?: number;
}

const OffHoursRecommendationsChartContent: FC<
  OffHoursRecommendationsChartProps
> = ({ data, lineInCenter, withDbConnections, chartHeight = 400 }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  const { yAxisCpuTicks, yAxisDbTicks } = useMemo(() => {
    return generateYAxisCpuAndDbTicks(data);
  }, [data]);

  const chartData = useMemo(() => {
    return generateOffHoursHighChartData(data, withDbConnections);
  }, [data, withDbConnections]);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    // @ts-expect-error  | Something wrong with types, this is waiting for "Options" but it should be an "Element"
    const chart = Highcharts.chart(chartRef.current, {
      chart: { type: "area", height: chartHeight },
      title: { text: "" },
      xAxis: {
        crosshair: { width: 1, color: "#000", dashStyle: "ShortDot" },
        categories: chartData.categories,
        labels: {
          formatter: function () {
            return formatDate(
              dateFromUtcByTimeZone(`${this.value}`) || moment(),
              { type: "dateTime" },
            );
          },
          style: {
            fontSize: "10px",
            color: OffHoursRecommendationsChartColors.TextColor,
          },
        },
        tickInterval: Math.max(1, Math.floor(data.length / 7) - 1),
      },
      yAxis: getYAxises(withDbConnections, yAxisCpuTicks, yAxisDbTicks),
      tooltip: offHoursChartTooltipOptions,
      plotOptions: {
        area: { marker: { enabled: false } },
        series: { animation: false },
      },
      series: chartData.series,
      credits: { enabled: false },
      legend: { enabled: false },
    });

    // Add center line if needed
    if (lineInCenter && data.length > 0) {
      const centerIndex = Math.round(data.length / 2);
      if (centerIndex < data.length) {
        chart.xAxis[0].addPlotLine({
          value: centerIndex,
          color: OffHoursRecommendationsChartColors.HorizontalLine,
          width: 1,
          label: {
            text: "Week",
            style: {
              color: OffHoursRecommendationsChartColors.TextColor,
              fontSize: "10px",
            },
            align: "center",
            y: 15,
          },
        });
      }
    }

    return () => {
      chart.destroy();
    };
  }, [
    chartData,
    data,
    lineInCenter,
    withDbConnections,
    yAxisCpuTicks,
    yAxisDbTicks,
    chartHeight,
  ]);

  return (
    <Stack>
      <Box ref={chartRef} width="100%" height={chartHeight} />
      <LegendContent withDbConnection={withDbConnections} />
    </Stack>
  );
};

export const OffHoursRecommendationsChart: FC<
  OffHoursRecommendationsChartProps
> = (props) => {
  return (
    <ChartErrorBoundary>
      <OffHoursRecommendationsChartContent {...props} />
    </ChartErrorBoundary>
  );
};
